import { homeSections } from './homeSections'
import type { PageContent } from '../core/types/pages'
import type { Tone } from '../ui/sections'

/**
 * De opbouw van elke pagina zoals de site hem toont (docs/VORMGEVING.md › Pagina's): één bron
 * voor de volgorde in de pagina-editor (Paginateksten). Per blok de titel, de rol in het verhaal,
 * de toon van het vlak en het ankertje op de pagina (voor "Bekijk dit blok").
 *
 * Meerdere tekstblokken kunnen bij één vlak op de site horen (bijv. "stappen" en "cadeaus" op
 * Protocollen): die delen dan het ankertje. `labels` = hoe een veld op díe plek heet, als het
 * algemene label (adminLabels.ts) niet genoeg zegt. De toon komt uit ui/sections.ts, dezelfde
 * die `Section` op de site gebruikt.
 *
 * `imageBetween`: tussen dit blok en het vorige staat een groot beeld (dan mag de toon gelijk zijn).
 * `sameSurface`: dit blok hoort bij hetzelfde vlak als het vorige (bijv. de kop en de collectie op
 * Webshop); geen nieuwe sectie, dus geen dubbele toon.
 */
export type PageBlock = {
  key: string
  title: string
  role: string
  tone?: Tone
  /** Ankertje op de pagina; leeg = bovenaan de pagina. */
  anchor: string
  labels?: Record<string, { label: string; hint?: string }>
  imageBetween?: boolean
  sameSurface?: boolean
  /** Tussen dit blok en het vorige staat een vlak zonder eigen paginatekst (bijv. het abonnement), met deze toon. */
  afterOther?: Tone
  /** Dit blok heeft "Plan een afspraak" als grote knop. Rustig: alleen in de opening en het slot. */
  bookButton?: boolean
}

export type EditablePage = Exclude<keyof PageContent, 'seo'>

export const pageSections: Partial<Record<EditablePage, readonly PageBlock[]>> = {
  home: homeSections,

  shop: [
    {
      key: 'hero',
      title: 'Kop',
      role: 'Wat past bij mijn huid? Hoofdknop “Kies op je huidvraag” (vast), de huidtest als tweede',
      tone: 'sand',
      anchor: '',
      labels: { secondaryCta: { label: 'Tweede knop (huidtest)', hint: 'Met het portret erbij. Leeg = niet tonen.' } },
    },
    { key: 'proof', title: 'Vertrouwensregel', role: 'De salon als bron van de keuze, onder de kop', tone: 'sand', anchor: '', sameSurface: true },
    // Tussen de collectie en de protocollen staat het grote beeld (W1) met de beloftes.
    {
      key: 'inUse',
      title: 'Groot beeld',
      role: 'Onder de collectie: een product in gebruik (W1). Leeg = het tijdelijke beeld',
      tone: 'sand',
      anchor: '',
      sameSurface: true,
    },
    { key: 'routines', title: 'Protocollen', role: 'Voor wie twijfelt tussen losse producten', tone: 'sand', anchor: 'shop-routines', imageBetween: true },
    { key: 'ingredients', title: 'Wat er écht in zit', role: 'Wáárom iets werkt, per stof', tone: 'shell', anchor: 'ingredients-title' },
    // Tussen ingrediënten en merken staat het abonnement op klei (tekst: Paginateksten › Bewust Abonnement).
    { key: 'brands', title: 'Merken', role: 'Waarom juist deze merken (de merken zelf: Aanbod › Merken)', tone: 'shell', anchor: 'shop-brands', afterOther: 'clay' },
  ],

  routines: [
    {
      key: 'hero',
      title: 'Opening',
      role: 'Wat gebruik ik samen? Hoofdknop “Begin met je protocol” (vast), de huidtest als tweede',
      tone: 'sand',
      anchor: '',
      labels: { cta: { label: 'Tweede knop (huidtest)' } },
    },
    { key: 'anchorEyebrow', title: 'Het complete protocol', role: 'Het pronkstuk; het protocol zelf beheer je bij Aanbod › Protocollen', tone: 'blush', anchor: 'anchor-routine' },
    {
      key: 'gifts',
      title: 'Het complete protocol · cadeaus en twijfel',
      role: 'Kop boven de cadeaus die dit protocol vrijspeelt (de treden: Instellingen), en de WhatsApp-regel onder de knop',
      tone: 'blush',
      anchor: 'anchor-routine',
      sameSurface: true,
      labels: { heading: { label: 'Kop boven de cadeaus' }, note: { label: 'Regel onder de knop (opent WhatsApp)' } },
    },
    {
      key: 'concerns',
      title: 'Per klacht',
      role: 'De klachtkiezer van Home, hier rustig: per klacht het protocol voor thuis en wat de salon erbij doet',
      tone: 'shell',
      anchor: 'concern',
    },
    { key: 'compareHeading', title: 'Vergelijken', role: 'De uitklapper onder de kiezer, met alle protocollen in een tabel', tone: 'shell', anchor: 'concern', sameSurface: true, labels: { compareHeading: { label: 'Knop van de uitklapper' } } },
    {
      key: 'smallStart',
      title: 'Klein beginnen',
      role: 'Onder de kiezer: los beginnen in de webshop (de huidtest staat al in de opening)',
      tone: 'shell',
      anchor: 'concern',
      sameSurface: true,
      labels: { body: { label: 'Zin' }, shopCta: { label: 'Link naar de webshop' } },
    },
    {
      key: 'buildUp',
      title: 'Na je bestelling',
      role: 'De tijdlijn van Home, hier stil. Eigen kop en zin; de momenten zelf pas je aan bij Home › Opbouwen',
      tone: 'sand',
      anchor: 'routines-build-up',
    },
    {
      key: 'salonClient',
      title: 'Al klant in de salon?',
      role: 'Afsluiter: de salon als bron',
      tone: 'blush',
      anchor: 'salon-client',
      // Ertussen: het abonnement (kort, shell) en de vragen.
      afterOther: 'shell',
      labels: { cta: { label: 'Knop (opent WhatsApp)' } },
    },
  ],

  treatments: [
    {
      key: 'hero',
      title: 'Opening',
      role: 'Kan ik langskomen? Hoofdknop: plan een afspraak',
      tone: 'sand',
      anchor: '',
      bookButton: true,
      labels: {
        cta: { label: 'Hoofdknop (afspraak plannen)' },
        secondaryCta: { label: 'Tweede knop (naar de lijst)' },
        treatedBy: { label: 'Regel bij de foto', hint: 'Gevolgd door de naam. Beloof niet dat je alles zelf doet: er werkt ook een collega.' },
      },
    },
    { key: 'listHeading', title: 'Alle behandelingen · kop', role: 'Wie hier komt, wil kiezen', tone: 'shell', anchor: 'behandelingen' },
    { key: 'includes', title: 'Alle behandelingen · inbegrepen', role: 'Wat bij elke behandeling zit', tone: 'shell', anchor: 'behandelingen', sameSurface: true },
    { key: 'team', title: 'Ook in de salon', role: 'Collega’s, alleen als ze op de site staan (Instellingen › Team)', tone: 'sand', anchor: 'salon-team' },
    {
      key: 'bindweefsel',
      title: 'Bindweefselmassage',
      role: 'Waarom het werkt: het ene verhaal op klei, het enige bewegende beeld',
      tone: 'clay',
      anchor: 'bindweefsel',
      labels: {
        cta: { label: 'Rustige link (boekt de bindweefselmassage)', hint: 'Een link, geen grote knop: de grote boekknoppen staan in de opening en het slot.' },
        secondaryCta: { label: 'Link naar de webshop' },
      },
    },
    {
      key: 'bridge',
      title: 'Van salon naar thuis',
      role: 'De brug naar de webshop: tekst en de merken, zonder tweede groot beeld',
      tone: 'sand',
      anchor: 'salon-bridge',
      labels: { cta: { label: 'Knop naar de protocollen' }, secondaryCta: { label: 'Tweede knop (webshop)' } },
    },
    { key: 'faqTitle', title: 'Praktisch', role: 'Kop boven de vragen over je eerste afspraak', tone: 'shell', anchor: 'salon-vragen' },
    {
      key: 'closing',
      title: 'Twijfel',
      role: 'Afsluiter op blush',
      tone: 'blush',
      anchor: 'salon-closing',
      bookButton: true,
      labels: { cta: { label: 'Knop (afspraak, met het consult gekozen)' }, secondaryCta: { label: 'WhatsApp-link' } },
    },
  ],

  journal: [
    { key: 'hero', title: 'Kop', role: 'Wat weet een huidspecialist dat ik nog niet weet?', tone: 'sand', anchor: '' },
    { key: 'articlesHeading', title: 'Artikelen · kop', role: 'Boven het nieuwste artikel en het raster', tone: 'sand', anchor: 'journal-articles', sameSurface: true },
    { key: 'emptyCategory', title: 'Artikelen · lege categorie', role: 'Als een categorie (nog) leeg is', tone: 'sand', anchor: 'journal-articles', sameSurface: true },
    // Het slot op blush: de footer is klei, dus klei hier zou erin overlopen.
    {
      key: 'podcast',
      title: 'De podcast en Instagram',
      role: 'Het slot op blush; onder de afleveringen één regel naar Instagram (de handle: Instellingen › Salon & contact)',
      tone: 'blush',
      anchor: 'podcast-title',
      labels: {
        url: { label: 'Link naar de show', hint: 'Ook de link van een aflevering zonder eigen link.' },
        instagramLine: { label: 'Zin vóór de Instagram-handle', hint: 'Leeg = de regel niet tonen. Geen feed: alleen een link.' },
      },
    },
    {
      key: 'episodes',
      title: 'Afleveringen',
      role: 'In de podcast, nieuwste eerst; elke aflevering een eigen link',
      tone: 'blush',
      anchor: 'podcast-title',
      sameSurface: true,
      labels: {
        url: { label: 'Link naar deze aflevering', hint: 'Leeg = de link naar de show.' },
        date: { label: 'Datum', hint: 'Bijv. “3 okt 2026”. Leeg = niet tonen.' },
      },
    },
  ],

  // Eén vraag: waarom zou ik háár vertrouwen met mijn huid? Wie · hoe het begon (met de perslijn) ·
  // waar ik voor sta · wat ik zelf gebruik · kom kennismaken. Alleen over Sarah: vaste cliënten
  // staan bij de reviews (Home en de productpagina).
  about: [
    {
      key: 'hero',
      title: 'Opening en hoe het begon',
      role: 'Wie is ze? Hoofdknop “Bekijk de collectie” (vast), een afspraak als tweede. Daaronder het verhaal.',
      tone: 'sand',
      anchor: '',
      labels: {
        cta: { label: 'Tweede knop (afspraak plannen)' },
        paragraphs: { label: 'Alinea’s', hint: 'De eerste staat in de opening. De rest is “hoe het begon”; de laatste staat er als slotzin.' },
      },
    },
    {
      key: 'credentials',
      title: 'Opleidingen',
      role: 'Naast de feiten: het bewijs van vakmanschap. De perslijn staat eronder (Instellingen › Pers)',
      tone: 'sand',
      anchor: '',
      sameSurface: true,
      labels: {
        heading: { label: 'Kop boven de lijst' },
        items: { label: 'Opleidingen (leeg = niet tonen)', hint: 'Eén regel per opleiding of certificaat, bijv. “Gediplomeerd schoonheidsspecialist”.' },
      },
    },
    { key: 'philosophy', title: 'Waar ik voor sta', role: 'Haar filosofie, op klei, het enige bewegende beeld', tone: 'clay', anchor: 'filosofie' },
    {
      key: 'shelf',
      title: 'Haar plank',
      role: 'Drie producten die ze zelf kiest, elk met waarom',
      tone: 'shell',
      anchor: 'about-shelf',
      labels: { picks: { label: 'Haar keuzes', hint: 'Een product dat uit de webshop is of uitverkocht, valt vanzelf weg.' } },
    },
    {
      key: 'salon',
      title: 'Kom kennismaken',
      role: 'Afsluiter op blush (het gedeelde blok “kom langs”)',
      tone: 'blush',
      anchor: 'about-salon',
      bookButton: true,
      labels: { cta: { label: 'Knop (afspraak plannen)' } },
    },
  ],

  contact: [
    {
      key: 'hero',
      title: 'Kop',
      role: 'Hoe krijg ik snel antwoord? Hoofdknop: WhatsApp',
      tone: 'sand',
      anchor: '',
      labels: { cta: { label: 'Hoofdknop (opent WhatsApp)' }, secondaryCta: { label: 'Tweede knop (mail)' } },
    },
    { key: 'channelsTitle', title: 'Soorten vragen · kop', role: 'Naast de kop', tone: 'sand', anchor: '', sameSurface: true },
    { key: 'channels', title: 'Soorten vragen', role: 'Elke vraag met de snelste weg', tone: 'sand', anchor: '', sameSurface: true },
    { key: 'door', title: 'Groot beeld', role: 'Vóór Langskomen: de salon of de voordeur (C1), zonder beweging. Leeg = het tijdelijke beeld', tone: 'sand', anchor: '', sameSurface: true },
    { key: 'visit', title: 'Langskomen', role: 'Kaart, route en tijden: de lange versie van “kom langs”', tone: 'blush', anchor: 'langskomen', imageBetween: true, bookButton: true },
  ],

  chrome: [
    {
      key: 'footer',
      title: 'Footer',
      role: 'Onder elke pagina, op klei: wie je bent en wanneer de salon open is',
      tone: 'clay',
      anchor: '',
      labels: {
        intro: { label: 'Zin onder de naam', hint: 'Eén of twee zinnen. {city} = de plaats uit Instellingen.' },
        hoursHeading: { label: 'Kop boven de tijden' },
      },
    },
  ],
}

/**
 * De regels voor het ritme van een pagina (docs/VORMGEVING.md › Kleur en ritme), als lijst met
 * problemen; leeg = in orde. Getest in data/pageSections.test.ts.
 * - Twee vlakken met dezelfde toon direct na elkaar, zonder groot beeld ertussen.
 * - Meer dan één klei-vlak (de footer telt niet: die staat buiten de pagina).
 */
export function rhythmProblems(blocks: readonly PageBlock[]): string[] {
  const surfaces = blocks
    .filter((b, i) => i === 0 || !b.sameSurface)
    .flatMap((b): PageBlock[] => (b.afterOther ? [{ key: '', title: 'een vlak zonder paginatekst', role: '', anchor: '', tone: b.afterOther }, b] : [b]))
  const problems: string[] = []
  surfaces.forEach((b, i) => {
    const prev = surfaces[i - 1]
    if (prev && prev.tone && prev.tone === b.tone && !b.imageBetween) problems.push(`${prev.title} en ${b.title} hebben allebei ${b.tone}`)
  })
  const clay = surfaces.filter((b) => b.tone === 'clay').length
  if (clay > 1) problems.push(`${clay} klei-vlakken`)
  return problems
}

/**
 * Grote boekknoppen alleen in de opening en het slot; ertussen is boeken een rustige link.
 * Home valt erbuiten (het speelse verhaal eindigt elk blok met een stap). Leeg = in orde.
 */
export function bookButtonProblems(blocks: readonly PageBlock[]): string[] {
  const last = blocks.length - 1
  return blocks.filter((b, i) => b.bookButton && i !== 0 && i !== last).map((b) => `${b.title} heeft een grote boekknop midden op de pagina`)
}
